import HighchartsReact, {
  HighchartsReactProps,
  HighchartsReactRefObject,
} from "highcharts-react-official"
import Highcharts, { SeriesBarOptions } from "highcharts"
import { useEffect, useMemo, useRef } from "react"



const COLOR_PALETTE = [
  "#184389",          // DARK_BLUE
  "#4B99F7",          // SKY_BLUE
  "#73C9F9",          // LIGHT_CYAN
  "#235CE8",          // BLUE
  "#00A6FB",          // CYAN
  "#1C98B5",          // TEAL
]

export interface TmIndexGraphProps {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id?: string

  /**
   * Object with vendors as key and value their respective score
   */
  vendors?: {
    name: string
    score: number
  }[]
  /**
   * Whether to generate SVG or not
   */
  generate_svg?: boolean
  /**
   * SVG string
   */
  svg?: string
  /**
   * Maximum height of the chart
   */
  max_height?: number
  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<TmIndexGraphProps>) => void
}

const getOptions = (
  data?: TmIndexGraphProps["vendors"],
  maxHeight: number = Infinity
): HighchartsReactProps["options"] => {
  const vendors: string[] = data ? data.map((vendor) => vendor.name) : []
  const scores: number[] = data ? data.map((vendor) => vendor.score) : []

  const height = data ? vendors.length * 75 + 20 : 100

  return {
    chart: {
      type: "bar",
      marginLeft: data ? 135 : undefined,
      height: Math.min(height, maxHeight),
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: data ? vendors : ["Your Weightings"],

      labels: {
        style: {
          color: "#000000",
          fontWeight: "400",
          fontSize: "14px",
        },
      },
    },
    yAxis: {
      min: 0,
      max: 100,
      tickInterval: 20,
      title: {
        text: "",
      },
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        stacking: "normal",
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          inside: true,
          format: "{y}%",
          style: {
            color: "#000000",
            textOutline: "none",
          },
        },
      },
    },
    accessibility: {
      enabled: false,
    },
    series: [
      {
        name: data ? "Total Score" : "",
        data: data
          ? scores.map((score, i) => ({
              y: score,
              color: COLOR_PALETTE[i % COLOR_PALETTE.length], // usa color secuencialmente
            }))
          : [100],
        color: !data ? "#00b4d8" : undefined, // AQUA BLUE
        maxPointWidth: 30,
      } as SeriesBarOptions,
    ],
    exporting: {
      enabled: false,
    },
  }
}

const TmIndexGraph = ({
  vendors,
  generate_svg,
  max_height = Infinity,
  setProps,
}: TmIndexGraphProps) => {
  if (generate_svg == null) generate_svg = false
  if (max_height == null) max_height = Infinity

  const chartRef = useRef<HighchartsReactRefObject>(null)

  const options = useMemo(() => getOptions(), [])
  const vendorOptions = useMemo(() => getOptions(vendors, max_height), [vendors, max_height])

  useEffect(() => {
    if (generate_svg) {
      const chart = chartRef.current?.chart
      if (chart != undefined) {
        setProps({ svg: chart.getSVG() })
      }
    }
  }, [generate_svg, vendorOptions])

  return (
    <div>
      <HighchartsReact highcharts={Highcharts} options={options} />
      <HighchartsReact highcharts={Highcharts} options={vendorOptions} ref={chartRef} />
    </div>
  )
}

export default TmIndexGraph
